'use client';

import { useEffect, useState } from 'react';

const svgSrcCache = new Map<string, string>();
const nonSvgSrcCache = new Set<string>();

const REMOTE_URL_PATTERN = /^https?:\/\//i;
const REMOTE_RASTER_PATTERN = /^https?:\/\/.+\.(?:png|jpe?g|gif|webp|avif|bmp|ico)(?:[?#].*)?$/i;

const createSvgDataUrl = (svgContent: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;

const shouldAttemptSvgInline = (src: string) => {
  if (!REMOTE_URL_PATTERN.test(src)) {
    return false;
  }

  if (REMOTE_RASTER_PATTERN.test(src)) {
    return false;
  }

  // GCS/Gumlet SVGs are cross-origin and fail CORS. The fetch cannot inline
  // them and contends with LCP (header logo). Only same-origin URLs are inlined.
  if (typeof window !== 'undefined') {
    try {
      if (new URL(src, window.location.href).origin !== window.location.origin) {
        return false;
      }
    } catch {
      return false;
    }
  }

  return true;
};

const useRenderableImageSrc = (src: string | undefined) => {
  const [resolvedSrc, setResolvedSrc] = useState(src ?? '');

  useEffect(() => {
    if (!src) {
      setResolvedSrc('');
      return;
    }

    if (!shouldAttemptSvgInline(src)) {
      setResolvedSrc(src);
      return;
    }

    if (nonSvgSrcCache.has(src)) {
      setResolvedSrc(src);
      return;
    }

    const cachedSrc = svgSrcCache.get(src);
    if (cachedSrc) {
      setResolvedSrc(cachedSrc);
      return;
    }

    // Keep showing the raw `src` (a valid, directly-renderable <img> URL) while
    // the inline fetch runs below — blanking it here made RenderableImage
    // return null mid-load, collapsing the image to 0x0 and causing a layout
    // shift when the inlined data URL popped back in.
    setResolvedSrc(src);
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(src);
        if (!response.ok) {
          nonSvgSrcCache.add(src);
          if (!cancelled) {
            setResolvedSrc(src);
          }
          return;
        }

        const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
        if (contentType.startsWith('image/') && !contentType.includes('svg')) {
          nonSvgSrcCache.add(src);
          if (!cancelled) {
            setResolvedSrc(src);
          }
          return;
        }

        const svgContent = await response.text();
        if (!svgContent.includes('<svg')) {
          nonSvgSrcCache.add(src);
          if (!cancelled) {
            setResolvedSrc(src);
          }
          return;
        }

        const dataUrl = createSvgDataUrl(svgContent);
        svgSrcCache.set(src, dataUrl);

        if (!cancelled) {
          setResolvedSrc(dataUrl);
        }
      } catch {
        nonSvgSrcCache.add(src);
        if (!cancelled) {
          setResolvedSrc(src);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src]);

  return resolvedSrc;
};

export default useRenderableImageSrc;
